/** @format */
import { useAuthContext } from '@mpb-com/auth-sdk/client';
import { Icon } from '@mpb-com/feather/components/atoms/icon';
import {
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon/types';
import { Trans, useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import { Heart } from '@toucan/client/global/icons/animated/heart';

import {
    ApplicationContext,
    ModalSize,
} from '@toucan/client/global/application';

import { Link } from '@toucan/client/shared/components/link';
import { useTooltip } from '@toucan/client/shared/hooks/use-tooltip';

import { SignUpFunnel } from '@toucan/client/blocks/components/sign-up-funnel';
import { useLogin } from '@toucan/client/blocks/hooks/use-login';

import { styles } from './toggle-favourite.styles';
import { StyledToggleFavouriteProps, ToggleFavouriteProps } from './types';

export enum TooltipContent {
    LOGIN_OR_SIGNUP = 'login_or_signup',
    SAVED = 'saved',
}

export enum ToggleFavouriteSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large',
}

const StyledToggleFavourite = styled('div')<StyledToggleFavouriteProps>`
    ${styles}
`;

export const ToggleFavourite: FunctionComponent<ToggleFavouriteProps> = ({
    label,
    isFavourite = false,
    disabled = false,
    successMessage,
    isLoading = false,
    onToggle,
    'data-testid': dataTestId = 'toggleFavourite',
    className,
    ...iconProps
}: ToggleFavouriteProps) => {
    const { t } = useTranslation();
    const { setModalVisible, setModalContent } = useContext(ApplicationContext);
    const { isLoggedIn } = useAuthContext();
    const [tooltipContent, setTooltipContent] = useState<TooltipContent | null>(
        null
    );

    const handleLogInSuccess = useCallback(() => {
        setTooltipContent(TooltipContent.SAVED);
        onToggle();
    }, [onToggle]);

    const { startLogin } = useLogin({ onLogInSuccess: handleLogInSuccess });

    const [bindToolTip, Tooltip, openTooltip, closeTooltip] =
        useTooltip<HTMLDivElement>({
            id: 'toggle-favourite-tooltip',
            manual: true,
            closeOnClickAway: true,
            onClickAway: () => setTooltipContent(null),
        });

    /**
     * Overrides onToggle with a prompt if the user is not logged in.
     */
    const handleToggleFavourite = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (disabled) {
            return;
        }

        if (!isLoggedIn) {
            setTooltipContent(TooltipContent.LOGIN_OR_SIGNUP);
            return;
        }

        if (!isFavourite) {
            setTooltipContent(TooltipContent.SAVED);
        }

        onToggle();
    };

    const renderTooltipLoginOrSignupContent = () => {
        function handleLoginClick(event: MouseEvent<HTMLElement>) {
            event.preventDefault();
            event.stopPropagation();

            setTooltipContent(null);
            startLogin();
        }

        function handleSignUpClick(event: MouseEvent<HTMLElement>) {
            event.preventDefault();
            event.stopPropagation();

            setTooltipContent(null);
            setModalContent(<SignUpFunnel />);
            setModalVisible(true, { size: ModalSize.LARGE });
        }

        const loginOrSignUpComponents = [
            <Link
                key="login"
                onClick={handleLoginClick}
                data-testid="toggle-favourite__tooltip-login"
            >
                $1
            </Link>,
            <Link
                key="signup"
                onClick={handleSignUpClick}
                data-testid="toggle-favourite__tooltip-signup"
            >
                $1
            </Link>,
        ];

        return (
            <Trans components={loginOrSignUpComponents}>
                {t(
                    'You need to be logged in to save things. <0>Log in</0> or <1>sign up</1> for an account.',
                    { context: 'favouriteProduct' }
                )}
            </Trans>
        );
    };

    const renderTooltipContent = () => {
        switch (tooltipContent) {
            case TooltipContent.LOGIN_OR_SIGNUP:
                return renderTooltipLoginOrSignupContent();
            case TooltipContent.SAVED:
                return successMessage;
            default:
                return null;
        }
    };

    const renderIcon = () => {
        return (
            <Icon
                variant={IconVariant.ACTIONABLE}
                size={IconSize.L}
                isLoading={isLoading}
                onClick={handleToggleFavourite}
                aria-label={label}
                title={label}
                aria-checked={isFavourite}
                isToggled={isFavourite}
                role="checkbox"
                {...iconProps}
                className="toggle-favourite-icon"
            >
                <Heart />
            </Icon>
        );
    };

    useEffect(() => {
        switch (tooltipContent) {
            case TooltipContent.LOGIN_OR_SIGNUP:
                openTooltip();
                return;
            case TooltipContent.SAVED:
                isFavourite && openTooltip();
                return;
            default:
                closeTooltip();
        }
    }, [tooltipContent, isFavourite]);

    return (
        <Fragment>
            <StyledToggleFavourite
                data-testid={dataTestId}
                className={className}
                {...bindToolTip}
                isFavourite={isFavourite}
            >
                {renderIcon()}
            </StyledToggleFavourite>
            {tooltipContent && <Tooltip>{renderTooltipContent()}</Tooltip>}
        </Fragment>
    );
};
